import { computed, makeObservable } from 'mobx'

import * as e from '~/client/src/shared/stores/EventStore/eventConstants'

import InitialState from '../InitialState'

export default class RoutesStore {
  public constructor(private readonly state: InitialState) {
    makeObservable(this)
  }

  /**
   * Whether the tenant bundle is still in its boot-up phase and routes
   * shouldn't render their content yet. Used by desktop.routes.tsx and
   * mobile.routes.tsx to gate the route Switch with a centered Loader.
   *
   * Two parts:
   *
   * 1. The initial bootstrap window — gated on `loading.get(INIT_APP)`.
   *    `INIT_APP_2` writes `INIT_APP=false` (commonEvents.ts:767) to
   *    signal that projects are loaded and the chain has moved past
   *    bootstrap; before that the flag is `true` (in-flight) or
   *    `undefined` (pre-fire, cache-restore window). `!== false` catches
   *    both pre-fire and in-flight.
   *
   *    Short-circuit exits (`getAvailableProjects: []`, invalid
   *    `initProjectCode`, `shouldInterruptInitialization`) all run
   *    through `INIT_COMPLETE`, which `loading.clear()`s the whole map.
   *    Without help that would wipe the explicit `false` back to
   *    `undefined` and re-engage this gate forever (STX1-508). The
   *    `INIT_COMPLETE` handler re-writes `INIT_APP=false` after the
   *    clear so this gate stays released on every exit path.
   *
   *    `didInitialize` is intentionally NOT used here. desktopEvents
   *    INIT_APP_3 (and mobileEvents) flip it back to `false` when
   *    `userActiveProjectSettings` isn't populated, which can recur
   *    during `ACTIVATE_PROJECT_SUCCESS` re-bootstrap and lock the
   *    gate on if we depended on it.
   *
   * 2. The per-project initialization chain. After INIT_APP_2 fires we
   *    still need to wait for project-scoped data (auth-user-project,
   *    project type options, project config, …) before rendering project
   *    views. INIT_APP_2 is true between commonEvents.ts:767 and
   *    INIT_APP_3 (line 766 in the bundle's events file).
   *    GET_AUTH_USER_PROJECT flips true→false in the same chain
   *    (commonEvents.ts:719 / 738) and bridges the gap between
   *    INIT_APP_2 done and LOAD_AND_LISTEN_TO_PROJECT_TYPE_OPTIONS
   *    starting; covering it here prevents the brief HomeView mount
   *    that would otherwise happen during ACTIVATE_PROJECT_SUCCESS
   *    after the URL transition but before the project-type-options
   *    flag rises (visible as HomeView → loader → HomeView flicker).
   *    LOAD_AND_LISTEN_TO_PROJECT_TYPE_OPTIONS flips true→false
   *    alongside, completing the chain before INIT_APP_3 fans out the
   *    rest of the per-project subscriptions.
   */
  @computed
  public get areProjectTypesLoading(): boolean {
    if (this.state.loading.get(e.INIT_APP) !== false) {
      return true
    }
    return (
      !!this.state.loading.get(e.INIT_APP_2) ||
      !!this.state.loading.get(e.GET_AUTH_USER_PROJECT) ||
      !!this.state.loading.get(e.LOAD_AND_LISTEN_TO_PROJECT_TYPE_OPTIONS)
    )
  }
}
